import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { 
  TrendingUp, TrendingDown, Calendar, BarChart3, Upload, 
  Target, Award, AlertTriangle, Download, Layers, DollarSign, Crown, LogOut, User, HelpCircle, ArrowLeft, Newspaper
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Cell
} from "recharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Page Components
import LandingPage from "./components/LandingPage";
import AuthModal from "./components/AuthModal";
import PricingPage from "./components/PricingPage";
import PromoCodeModal from "./components/PromoCodeModal";
import TermsOfService from "./components/TermsOfService";
import PrivacyPolicy from "./components/PrivacyPolicy";
import ContactPage from "./components/ContactPage";
import ExchangeGuide from "./components/ExchangeGuide";
import HelpPage from "./components/HelpPage";

// Dashboard Components
import { StatCard } from "./components/StatCard";
import { CalendarPNL } from "./components/CalendarPNL";
import { CustomTooltip } from "./components/CustomTooltip";
import { MonthlyComparison } from "./components/MonthlyComparison";
import { AssetTrend } from "./components/AssetTrend";
import { PnlVsBitcoin } from "./components/PnlVsBitcoin";
import { QuarterlyComparison } from "./components/QuarterlyComparison";
import { WinRateEvolution } from "./components/WinRateEvolution";
import { BestWorstMonth } from "./components/BestWorstMonth";
import { OpenPositions } from "./components/OpenPositions";
import { PnlByAssetChart } from "./components/PnlByAssetChart";

import AdminPanel from "./components/AdminPanel";
import TerminalPage from "./components/TerminalPage";
import TerminalLandingPage from "./components/TerminalLandingPage";
import PolymarketPage from "./components/PolymarketPage";

import { API } from "./constants";

function App() {
  // Auth state
  const [user, setUser] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('user');
      return saved ? JSON.parse(saved) : null;
    }
    return null;
  });
  const [showAuth, setShowAuth] = useState(false);
  const [authRedirect, setAuthRedirect] = useState(null);
  const [authMode, setAuthMode] = useState('register');
  const [currentPage, setCurrentPage] = useState('landing');
  const [uploadLimited, setUploadLimited] = useState(null);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [pendingPromoCode, setPendingPromoCode] = useState(null);

  // Dashboard state
  const [analytics, setAnalytics] = useState(null);
  const [assetData, setAssetData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [quarterlyData, setQuarterlyData] = useState(null);
  const [winRateData, setWinRateData] = useState(null);
  const [btcComparisonData, setBtcComparisonData] = useState(null);
  const [openPositions, setOpenPositions] = useState(null);
  const [pnlByAssetData, setPnlByAssetData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [activeView, setActiveView] = useState('calendar');
  const [capitalInicial, setCapitalInicial] = useState(() => {
    if (typeof window !== 'undefined') return localStorage.getItem('capitalInicial') || '';
    return '';
  });
  const [stakingPnl, setStakingPnl] = useState(() => {
    if (typeof window !== 'undefined') return localStorage.getItem('stakingPnl') || '';
    return '';
  });
  const reportRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (!token || !savedUser) return;
    // Validar la sesión contra el backend: si el pase caducó, limpiar y
    // recordar el email para ofrecer "Inicia sesión" (no "Crear cuenta").
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        localStorage.setItem('user', JSON.stringify(res.data));
        setUser(res.data);
        setCurrentPage('dashboard');
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          try {
            const email = JSON.parse(savedUser)?.email;
            if (email) localStorage.setItem('lastEmail', email);
          } catch (_) { /* ignore */ }
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        } else {
          // Error de red o del servidor: mantener la sesión local
          setUser(JSON.parse(savedUser));
          setCurrentPage('dashboard');
        }
      });
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setCurrentPage('landing');
    setAnalytics(null);
  };

  const handleAuthSuccess = async (userData) => {
    setUser(userData);
    setShowAuth(false);
    const pendingCode = localStorage.getItem('pendingPromoCode');
    if (pendingCode && userData.plan === 'free') {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(`${API}/promo/redeem`, { code: pendingCode }, { headers: { Authorization: `Bearer ${token}` } });
        userData.plan = response.data.plan;
        userData.plan_expires = response.data.plan_expires;
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        localStorage.removeItem('pendingPromoCode');
        setPendingPromoCode(null);
        alert(`¡Código canjeado! Tienes ${response.data.days_granted} días de acceso premium`);
      } catch (err) {
        console.error('Error redeeming promo code:', err);
        localStorage.removeItem('pendingPromoCode');
      }
    }
    setCurrentPage(authRedirect || 'dashboard');
    setAuthRedirect(null);
  };

  const handlePromoCodeFromLanding = (code) => {
    setPendingPromoCode(code);
    setShowPromoModal(false);
    setAuthMode('register');
    setShowAuth(true);
  };

  const handlePricingSuccess = (userData) => {
    setUser(userData);
    setCurrentPage('dashboard');
    setUploadLimited(null);
  };

  useEffect(() => { if (typeof window !== 'undefined') localStorage.setItem('capitalInicial', capitalInicial); }, [capitalInicial]);
  useEffect(() => { if (typeof window !== 'undefined') localStorage.setItem('stakingPnl', stakingPnl); }, [stakingPnl]);

  const stakingValue = parseFloat(stakingPnl) || 0;
  const totalPnlConStaking = (analytics?.summary?.total_pnl || 0) + stakingValue;

  const calcularRendimiento = () => {
    if (!capitalInicial) return null;
    const capital = parseFloat(capitalInicial);
    if (capital <= 0 || isNaN(capital)) return null;
    return (totalPnlConStaking / capital) * 100;
  };
  const rendimientoPersonalizado = calcularRendimiento();

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      const qs = params.toString();
      const token = localStorage.getItem('token');
      const authHeaders = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const [analyticsRes, assetRes, monthlyRes, quarterlyRes, winRateRes, btcRes, openPosRes, pnlByAssetRes] = await Promise.all([
        axios.get(`${API}/trading-analytics?${qs}`, authHeaders),
        axios.get(`${API}/asset-trend?${qs}`, authHeaders),
        axios.get(`${API}/monthly-comparison?${qs}`, authHeaders),
        axios.get(`${API}/quarterly-comparison?${qs}`, authHeaders),
        axios.get(`${API}/win-rate-evolution?${qs}`, authHeaders),
        axios.get(`${API}/pnl-vs-bitcoin?${qs}`, authHeaders),
        axios.get(`${API}/open-positions`, authHeaders),
        axios.get(`${API}/pnl-by-asset-time?${qs}`, authHeaders)
      ]);
      setAnalytics(analyticsRes.data);
      // Set calendar to last month of the data period
      const endDate = analyticsRes.data?.date_range?.end;
      if (endDate) {
        const parts = endDate.split('-');
        setCurrentMonth(new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, 1));
      }
      setAssetData(assetRes.data);
      setMonthlyData(monthlyRes.data);
      setQuarterlyData(quarterlyRes.data);
      setWinRateData(winRateRes.data);
      setBtcComparisonData(btcRes.data);
      setOpenPositions(openPosRes.data);
      setPnlByAssetData(pnlByAssetRes.data);
      setError(null);
    } catch (e) {
      console.error("Error fetching analytics:", e);
      if (e.response?.data?.error !== "No trading data found") setError("Error cargando los datos");
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const [unsupportedExchange, setUnsupportedExchange] = useState(null);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { setError("El archivo es demasiado grande. Máximo 50MB."); return; }
    setUploading(true); setUploadLimited(null); setError(null); setUnsupportedExchange(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'multipart/form-data' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await axios.post(`${API}/upload-trading-data`, formData, { headers, timeout: 300000 });
      if (response.data.unsupported_exchange) {
        setUnsupportedExchange(response.data.message);
        return;
      }
      if (response.data.limited) {
        setUploadLimited({ message: response.data.message, originalCount: response.data.original_count, limitedCount: response.data.records_count });
      }
      await fetchAnalytics();
      setError(null);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Error subiendo el archivo";
      if (e.code === 'ECONNABORTED') setError("Tiempo de espera agotado. Intenta de nuevo con un archivo más pequeño.");
      else if (e.response?.status === 401) {
        setError(msg);
        localStorage.removeItem('token');
        setTimeout(() => window.location.reload(), 2500);
      }
      else setError(msg);
      console.error(e);
    } finally {
      setUploading(false);
    }
  };

  const exportToPDF = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = 210, pageHeight = 297, margin = 8;
      const contentWidth = pageWidth - (margin * 2);
      let currentY = margin;
      pdf.setFillColor(10, 10, 11);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      const captureAndAdd = async (element, maxHeightMM = 140) => {
        if (!element) return 0;
        const canvas = await html2canvas(element, { scale: 1.5, backgroundColor: '#0a0a0b', logging: false, useCORS: true, allowTaint: true });
        const imgWidth = contentWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const finalHeight = Math.min(imgHeight, maxHeightMM);
        if (currentY + finalHeight > pageHeight - margin) {
          pdf.addPage(); pdf.setFillColor(10, 10, 11); pdf.rect(0, 0, pageWidth, pageHeight, 'F'); currentY = margin;
        }
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.85), 'JPEG', margin, currentY, imgWidth, finalHeight);
        currentY += finalHeight + 4;
        return finalHeight;
      };
      pdf.setFontSize(18); pdf.setTextColor(100, 149, 237); pdf.text('Trading Report', margin, currentY + 6);
      pdf.setFontSize(9); pdf.setTextColor(120, 120, 120);
      const dateText = analytics?.date_range?.start && analytics?.date_range?.end ? `${analytics.date_range.start} - ${analytics.date_range.end} · ${analytics?.broker || 'Trading'}` : analytics?.broker || 'Trading';
      pdf.text(dateText, margin, currentY + 12); currentY += 18;
      const summaryBanner = reportRef.current.querySelector('[data-testid="summary-banner"]');
      await captureAndAdd(summaryBanner, 45);
      const grids = reportRef.current.querySelectorAll('.grid.grid-cols-2');
      for (let i = 0; i < Math.min(grids.length, 2); i++) await captureAndAdd(grids[i], 55);
      const bestWorstGrid = reportRef.current.querySelector('.grid.grid-cols-1.md\\:grid-cols-2.gap-4.mb-8');
      if (bestWorstGrid) await captureAndAdd(bestWorstGrid, 50);
      const chartSections = reportRef.current.querySelectorAll('[data-testid="calendar-pnl"], .bg-\\[\\#0d0d0f\\].rounded-xl.border.border-gray-800.p-6');
      for (const section of chartSections) { if (section.offsetHeight < 100) continue; await captureAndAdd(section, 130); }
      pdf.save(`trading-report-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (e) {
      console.error("Error exporting PDF:", e); setError("Error al exportar PDF");
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatPNL = (value) => {
    if (value === undefined || value === null) return '0.00';
    const formatted = Math.abs(value).toFixed(4);
    return value >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  // Landing page
  if (currentPage === 'landing' && !user) {
    return (
      <>
        <LandingPage 
          onGetStarted={() => { setAuthMode('register'); setShowAuth(true); }}
          onLogin={() => { setAuthMode('login'); setShowAuth(true); }}
          onPromoCode={() => setShowPromoModal(true)}
          onAdmin={() => setCurrentPage('admin')}
          onNavigate={(page) => setCurrentPage(page)}
        />
        {showPromoModal && <PromoCodeModal onClose={() => setShowPromoModal(false)} onNeedRegister={handlePromoCodeFromLanding} />}
        {showAuth && <AuthModal mode={authMode} initialEmail={localStorage.getItem('lastEmail') || ''} onClose={() => { setShowAuth(false); setPendingPromoCode(null); localStorage.removeItem('pendingPromoCode'); }} onSuccess={handleAuthSuccess} onNavigate={(page) => { setShowAuth(false); setCurrentPage(page); }} />}
      </>
    );
  }

  // Terms and Privacy pages
  if (currentPage === 'terms') {
    return <TermsOfService onBack={() => setCurrentPage(user ? 'dashboard' : 'landing')} />;
  }
  if (currentPage === 'privacy') {
    return <PrivacyPolicy onBack={() => setCurrentPage(user ? 'dashboard' : 'landing')} />;
  }
  if (currentPage === 'contact') {
    return <ContactPage onBack={() => setCurrentPage(user ? 'dashboard' : 'landing')} />;
  }
  if (currentPage === 'help') {
    return <HelpPage onBack={() => setCurrentPage(user ? 'dashboard' : 'landing')} onNavigate={(page) => setCurrentPage(page)} />;
  }

  // Admin panel
  if (currentPage === 'admin') {
    return <AdminPanel onBack={() => setCurrentPage(user ? 'dashboard' : 'landing')} />;
  }

  // Terminal de mercado (noticias + whale tracker)
  if (currentPage === 'terminal') {
    return <TerminalPage onBack={() => setCurrentPage(user ? 'dashboard' : 'landing')} />;
  }

  // Polymarket — resultados de cartera
  if (currentPage === 'polymarket') {
    return <PolymarketPage onBack={() => setCurrentPage(user ? 'dashboard' : 'landing')} />;
  }

  // Landing del Terminal de mercado (página de ventas)
  if (currentPage === 'terminal-landing') {
    return (
      <>
        <TerminalLandingPage
          onBack={() => setCurrentPage('landing')}
          onEnter={() => {
            if (user) {
              setCurrentPage('terminal');
            } else {
              // Si ya ha tenido cuenta aquí, ofrecer "Inicia sesión" con el email precargado
              setAuthMode(localStorage.getItem('lastEmail') ? 'login' : 'register');
              setAuthRedirect('terminal');
              setShowAuth(true);
            }
          }}
          onRegister={() => {
            setAuthMode(localStorage.getItem('lastEmail') ? 'login' : 'register');
            setAuthRedirect('terminal');
            setShowAuth(true);
          }}
        />
        {showAuth && <AuthModal mode={authMode} initialEmail={localStorage.getItem('lastEmail') || ''} onClose={() => { setShowAuth(false); setPendingPromoCode(null); localStorage.removeItem('pendingPromoCode'); }} onSuccess={handleAuthSuccess} onNavigate={(page) => { setShowAuth(false); setCurrentPage(page); }} />}
      </>
    );
  }

  // Pricing page
  if (currentPage === 'pricing') {
    return <PricingPage user={user} onBack={() => setCurrentPage('dashboard')} onSuccess={handlePricingSuccess} />;
  }

  // Upload screen
  if (!analytics || analytics.error) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center p-6">
        <div className="bg-[#0d0d0f] border border-gray-800 rounded-2xl p-10 max-w-md w-full text-center relative">
          {user && (
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-800">
              <button
                onClick={() => setCurrentPage('landing')}
                className="flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors text-sm"
                data-testid="upload-back-btn"
              >
                <ArrowLeft className="w-4 h-4" /> Inicio
              </button>
              <div className="flex items-center gap-3">
                <span className="text-gray-400 text-sm">{user.name}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-gray-800 text-gray-500">{user.plan === 'free' ? 'Free' : 'Premium'}</span>
                <button onClick={handleLogout} className="text-gray-500 hover:text-white transition-colors" data-testid="upload-logout-btn" title="Cerrar sesión"><LogOut className="w-4 h-4" /></button>
              </div>
            </div>
          )}
          <div className="w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Upload className="w-10 h-10 text-cyan-400" />
          </div>
          <img src="/logo.png" alt="TraceMetrics" className="h-16 mx-auto mb-4" />
          <p className="text-gray-400 mb-8">Sube tu archivo de operaciones de MEXC (Excel) o Quantfury (PDF) para generar el informe</p>
          {user?.plan === 'free' && (
            <div className="mb-6 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-yellow-400 text-sm">
              Plan gratuito: máximo 20 operaciones.{' '}
              <button onClick={() => setCurrentPage('pricing')} className="underline hover:no-underline">Actualizar plan</button>
            </div>
          )}
          <label data-testid="upload-button" className="cursor-pointer inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-white rounded-lg font-medium transition-colors">
            <Upload className="w-5 h-5" />
            {uploading ? 'Subiendo...' : 'Seleccionar Archivo (Excel/PDF)'}
            <input type="file" accept=".xlsx,.xls,.pdf" onChange={handleFileUpload} className="hidden" disabled={uploading} />
          </label>
          {error && <p className="mt-4 text-red-400 text-sm">{error}</p>}
          {unsupportedExchange && (
            <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl text-left" data-testid="unsupported-exchange-msg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-amber-400 font-medium text-sm mb-1">Exchange no soportado</p>
                  <p className="text-gray-300 text-sm leading-relaxed">{unsupportedExchange}</p>
                </div>
              </div>
            </div>
          )}
          <ExchangeGuide />
        </div>
      </div>
    );
  }

  const { summary, best_day, worst_day, best_weekday, worst_weekday, daily_data, day_of_week_stats, trading_pairs, calendar_data } = analytics;
  const isPremium = user?.plan && user.plan !== 'free';

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      {uploadLimited && (
        <div className="bg-yellow-500/10 border-b border-yellow-500/20 px-6 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <p className="text-yellow-400 text-sm">{uploadLimited.message}</p>
            <button onClick={() => setCurrentPage('pricing')} className="px-4 py-1 bg-yellow-500 text-black rounded-lg text-sm font-medium hover:bg-yellow-400 transition-colors">Actualizar Plan</button>
          </div>
        </div>
      )}
      
      <header className="border-b border-gray-800 bg-[#0d0d0f]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/logo.png" alt="TraceMetrics" className="h-12 w-auto" />
              <button onClick={() => setCurrentPage('help')} className="flex items-center gap-1.5 px-3 py-1.5 text-gray-400 hover:text-cyan-400 hover:bg-gray-800/60 rounded-lg text-sm transition-colors" data-testid="dashboard-help-btn" title="Centro de Ayuda">
                <HelpCircle className="w-4 h-4" /> <span className="hidden sm:inline">Ayuda</span>
              </button>
              <button onClick={() => setCurrentPage('terminal')} className="flex items-center gap-1.5 px-3 py-1.5 text-gray-400 hover:text-cyan-400 hover:bg-gray-800/60 rounded-lg text-sm transition-colors" data-testid="dashboard-terminal-btn" title="Terminal de mercado: noticias y ballenas">
                <Newspaper className="w-4 h-4" /> <span className="hidden sm:inline">Terminal</span>
              </button>
              <p className="text-gray-500 text-sm">
                {analytics?.date_range?.start && analytics?.date_range?.end ? `${formatDate(analytics.date_range.start)} - ${formatDate(analytics.date_range.end)} · ${analytics?.broker || 'Trading'}` : analytics?.broker || 'Trading'}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-gray-900 rounded-lg px-3 py-2">
                <input type="date" value={dateRange.start} onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))} className="bg-transparent text-gray-300 text-sm border-none outline-none" data-testid="date-start" />
                <span className="text-gray-600">→</span>
                <input type="date" value={dateRange.end} onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))} className="bg-transparent text-gray-300 text-sm border-none outline-none" data-testid="date-end" />
              </div>
              <button onClick={exportToPDF} disabled={exporting} data-testid="export-pdf-btn" className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-800 disabled:cursor-not-allowed rounded-lg text-sm font-medium transition-colors">
                <Download className="w-4 h-4" /> {exporting ? 'Exportando...' : 'Exportar PDF'}
              </button>
              <label className="cursor-pointer flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors">
                <Upload className="w-4 h-4" /> Nuevo archivo
                <input type="file" accept=".xlsx,.xls,.pdf" onChange={handleFileUpload} className="hidden" disabled={uploading} />
              </label>
              {!isPremium && (
                <button onClick={() => setCurrentPage('pricing')} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 rounded-lg text-sm font-medium transition-colors">
                  <Crown className="w-4 h-4" /> Actualizar
                </button>
              )}
              {user && (
                <div className="flex items-center gap-3 pl-4 border-l border-gray-700">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <div className="hidden sm:block">
                      <p className="text-sm font-medium text-white">{user.name}</p>
                      <p className="text-xs text-gray-500">{isPremium ? <span className="text-cyan-400">Premium</span> : 'Plan Free'}</p>
                    </div>
                  </div>
                  <button onClick={handleLogout} className="p-2 hover:bg-gray-800 rounded-lg transition-colors" title="Cerrar sesión">
                    <LogOut className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main ref={reportRef} className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
          </div>
        ) : (
          <>
            {/* Summary Banner */}
            <div data-testid="summary-banner" className={`rounded-xl p-6 mb-8 ${totalPnlConStaking >= 0 ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              {analytics?.trade_type === 'spot' && (
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-3 py-1 bg-cyan-500/20 border border-cyan-500/40 rounded-full text-cyan-400 text-sm font-medium">SPOT Trading (FIFO)</span>
                  {openPositions?.positions_count > 0 && (
                    <span className="px-3 py-1 bg-purple-500/20 border border-purple-500/40 rounded-full text-purple-400 text-sm font-medium">{openPositions.positions_count} posiciones abiertas</span>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-gray-400 text-sm mb-1">{analytics?.trade_type === 'spot' ? 'PNL Realizado' : 'PNL Trading'}</p>
                  <span className={`text-3xl font-bold ${summary.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatPNL(summary.total_pnl)}</span>
                </div>
                {analytics?.trade_type === 'spot' && openPositions?.total_unrealized_pnl !== undefined && (
                  <div className="text-center">
                    <p className="text-gray-400 text-sm mb-1">PNL No Realizado</p>
                    <span className={`text-2xl font-bold ${openPositions.total_unrealized_pnl >= 0 ? 'text-cyan-400' : 'text-orange-400'}`}>
                      {openPositions.total_unrealized_pnl >= 0 ? '+' : ''}{openPositions.total_unrealized_pnl.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="text-center">
                  <p className="text-gray-400 text-sm mb-2">PNL Staking</p>
                  <input type="number" value={stakingPnl} onChange={(e) => setStakingPnl(e.target.value)} placeholder="0" className="w-24 px-2 py-2 bg-gray-900/80 border border-gray-700 rounded-lg text-white text-center text-lg font-medium focus:outline-none focus:border-purple-500 placeholder:text-gray-600" data-testid="staking-pnl-input" />
                </div>
                <div className="text-center">
                  <p className="text-gray-400 text-sm mb-1">PNL Total</p>
                  <div className="flex items-center gap-2">
                    <span className={`text-3xl font-bold ${totalPnlConStaking >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {totalPnlConStaking >= 0 ? '+' : ''}{totalPnlConStaking.toFixed(2)}
                    </span>
                    {totalPnlConStaking >= 0 ? <TrendingUp className="w-6 h-6 text-emerald-400" /> : <TrendingDown className="w-6 h-6 text-red-400" />}
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-gray-400 text-sm mb-2">Capital Inicial</p>
                  <input type="number" value={capitalInicial} onChange={(e) => setCapitalInicial(e.target.value)} placeholder="Ej: 10000" className="w-28 px-2 py-2 bg-gray-900/80 border border-gray-700 rounded-lg text-white text-center text-lg font-medium focus:outline-none focus:border-blue-500 placeholder:text-gray-600" data-testid="capital-inicial-input" />
                </div>
                <div className="text-center">
                  <p className="text-gray-400 text-sm mb-2">Capital Final</p>
                  {capitalInicial ? (
                    <div data-testid="capital-final-display" className={`w-28 px-2 py-2 rounded-lg text-center text-lg font-medium border ${(parseFloat(capitalInicial) + totalPnlConStaking) >= parseFloat(capitalInicial) ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400' : 'border-red-500/40 bg-red-500/10 text-red-400'}`}>
                      {(parseFloat(capitalInicial) + totalPnlConStaking).toFixed(0)}
                    </div>
                  ) : (
                    <div className="w-28 px-2 py-2 bg-gray-900/80 border border-gray-700 rounded-lg text-gray-500 text-center text-lg font-medium">--</div>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-gray-400 text-sm mb-1">Rendimiento</p>
                  {rendimientoPersonalizado !== null ? (
                    <p className={`text-3xl font-bold ${rendimientoPersonalizado >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {rendimientoPersonalizado >= 0 ? '+' : ''}{rendimientoPersonalizado.toFixed(2)}%
                    </p>
                  ) : (
                    <p className="text-3xl font-bold text-gray-500">--%</p>
                  )}
                </div>
              </div>
            </div>

            {openPositions && openPositions.trade_type === 'spot' && <OpenPositions openPositionsData={openPositions} onRefresh={fetchAnalytics} />}
            {pnlByAssetData && pnlByAssetData.trade_type === 'spot' && pnlByAssetData.chart_data?.length > 0 && <PnlByAssetChart data={pnlByAssetData} />}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <StatCard title="Total Operaciones" value={summary.total_trades} icon={BarChart3} color="blue" />
              <StatCard title="Operaciones Ganadas" value={summary.total_wins} subtitle={`${summary.win_rate}% win rate`} icon={TrendingUp} trend="up" color="green" />
              <StatCard title="Operaciones Perdidas" value={summary.total_trades - summary.total_wins} subtitle={`${(100 - summary.win_rate).toFixed(2)}% loss rate`} icon={TrendingDown} trend="down" color="red" />
              <StatCard title="Volumen Total" value={`${(summary.total_volume / 1000000).toFixed(2)}M`} subtitle="USDT tradeados" icon={DollarSign} color="orange" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard title="Días de Trading" value={summary.trading_days} icon={Calendar} subtitle={`${summary.winning_days} ganadores`} color="purple" />
              <StatCard title="Promedio Diario" value={formatPNL(summary.avg_daily_pnl)} subtitle="USDT/día" icon={Target} trend={summary.avg_daily_pnl >= 0 ? 'up' : 'down'} color={summary.avg_daily_pnl >= 0 ? 'green' : 'red'} />
              <StatCard title="Mejor Día" value={formatPNL(best_day?.pnl)} subtitle={formatDate(best_day?.date)} icon={Award} trend="up" color="green" />
              <StatCard title="Peor Día" value={formatPNL(worst_day?.pnl)} subtitle={formatDate(worst_day?.date)} icon={AlertTriangle} trend="down" color="red" />
            </div>

            {monthlyData && monthlyData.months && monthlyData.months.length > 1 && <BestWorstMonth monthlyData={monthlyData} />}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="bg-[#0d0d0f] rounded-xl border border-gray-800 p-5">
                <h4 className="text-gray-400 text-sm mb-3">Mejor Día de la Semana (Promedio)</h4>
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold text-white">{best_weekday?.day}</span>
                  <span className="text-emerald-400 font-semibold">+{best_weekday?.avg_pnl?.toFixed(2)} USDT/día</span>
                </div>
              </div>
              <div className="bg-[#0d0d0f] rounded-xl border border-gray-800 p-5">
                <h4 className="text-gray-400 text-sm mb-3">Peor Día de la Semana (Promedio)</h4>
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold text-white">{worst_weekday?.day}</span>
                  <span className="text-red-400 font-semibold">{worst_weekday?.avg_pnl?.toFixed(2)} USDT/día</span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {['calendar', 'bars', 'comparison', 'quarterly'].map((view) => (
                <button key={view} onClick={() => setActiveView(view)} data-testid={`view-${view}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${activeView === view ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                  {view === 'calendar' && <><Calendar className="w-4 h-4" /> Calendario</>}
                  {view === 'bars' && <><BarChart3 className="w-4 h-4" /> Barras</>}
                  {view === 'comparison' && <><Layers className="w-4 h-4" /> Mensual</>}
                  {view === 'quarterly' && <><Layers className="w-4 h-4" /> Trimestral</>}
                </button>
              ))}
            </div>

            {activeView === 'calendar' ? (
              <CalendarPNL calendarData={calendar_data || []} currentMonth={currentMonth} setCurrentMonth={setCurrentMonth} />
            ) : activeView === 'bars' ? (
              <div className="bg-[#0d0d0f] rounded-xl border border-gray-800 p-6 mb-8">
                <h3 className="text-lg font-semibold text-white mb-4">PNL Diario</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={daily_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1f1f23" />
                      <XAxis dataKey="date" stroke="#6b7280" tick={{ fill: '#6b7280', fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                      <YAxis stroke="#6b7280" tick={{ fill: '#6b7280', fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="pnl" name="PNL Diario">
                        {daily_data?.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.pnl >= 0 ? '#10b981' : '#ef4444'} />))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : activeView === 'comparison' ? (
              <MonthlyComparison monthlyData={monthlyData} />
            ) : (
              <QuarterlyComparison quarterlyData={quarterlyData} />
            )}

            <div className="mt-8"><WinRateEvolution winRateData={winRateData} /></div>
            <div className="mt-8"><PnlVsBitcoin btcData={btcComparisonData} /></div>
            <div className="mt-8"><AssetTrend assetData={assetData} capitalInicial={capitalInicial} stakingPnl={stakingPnl} totalPnlConStaking={totalPnlConStaking} rendimiento={rendimientoPersonalizado} /></div>

            <div className="bg-[#0d0d0f] rounded-xl border border-gray-800 p-6 mt-8 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">PNL Acumulado</h3>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-gray-400">PNL Acumulado</span>
                  <span className={summary.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}>{formatPNL(summary.total_pnl)} USDT</span>
                </div>
              </div>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={daily_data}>
                    <defs><linearGradient id="colorPnl" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/><stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f1f23" />
                    <XAxis dataKey="date" stroke="#6b7280" tick={{ fill: '#6b7280', fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis stroke="#6b7280" tick={{ fill: '#6b7280', fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="cumulative" name="PNL Acumulado" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorPnl)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-[#0d0d0f] rounded-xl border border-gray-800 p-6 mb-8">
              <h3 className="text-lg font-semibold text-white mb-4">Rendimiento por Día de la Semana</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={day_of_week_stats} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f1f23" />
                    <XAxis type="number" stroke="#6b7280" tick={{ fill: '#6b7280', fontSize: 11 }} />
                    <YAxis dataKey="day" type="category" stroke="#6b7280" tick={{ fill: '#6b7280', fontSize: 11 }} width={80} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="avg_pnl" name="Promedio PNL" radius={[0, 4, 4, 0]}>
                      {day_of_week_stats?.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.avg_pnl >= 0 ? '#10b981' : '#ef4444'} />))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-[#0d0d0f] rounded-xl border border-gray-800 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Rendimiento por Par de Trading</h3>
              <div className="space-y-3">
                {trading_pairs?.slice(0, 10).map((pair, idx) => (
                  <div key={pair.pair} className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500 text-sm w-6">#{idx + 1}</span>
                      <span className="text-white font-medium">{pair.pair}</span>
                    </div>
                    <span className={`font-semibold ${pair.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatPNL(pair.pnl)} USDT</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </main>

      <footer className="border-t border-gray-800 py-6 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center text-gray-500 text-sm">Trading Dashboard · Generado automáticamente</div>
      </footer>
    </div>
  );
}

export default App;
